import axios from 'axios';
import { getMsalInstance } from './AuthProvider';
import { loginRequest } from './authConfig';

/**
 * Authenticated HTTP client that adds the Authorization header with bearer token
 */
export const authApiClient = axios.create();

// Flag to prevent multiple redirects at once
let isRedirecting = false;

// Function to handle logging out and redirect to login
const redirectToLogin = async () => {
    if (isRedirecting) return;
    isRedirecting = true;
    
    console.log("API: Redirecting to login page");
    
    // First clear all stored auth data
    sessionStorage.removeItem("accessToken");
    sessionStorage.removeItem("idToken");
    sessionStorage.removeItem("tokenExpiresAt");
    sessionStorage.removeItem("loginState");
    sessionStorage.removeItem("loginNonce");
    
    // Clear MSAL caches before redirecting
    try {
        console.log("API: Clearing MSAL cache before redirect");
        const msalInstance = getMsalInstance();
        if (msalInstance) {
            msalInstance.clearCache();
            await msalInstance.logout({
                onRedirectNavigate: () => false // Don't redirect yet, we'll do it manually
            });
        }
    } catch (e) {
        console.error("API: Error clearing cache:", e);
    }
    
    // Set a flag in sessionStorage to indicate we're expecting a login
    sessionStorage.setItem("redirectedForLogin", "true");
    
    // Redirect to login page using dynamic base path
    const { navigateToPath } = require('../utils/pathUtils');
    navigateToPath('/');
};

// Add request interceptor to add auth token to all API requests
authApiClient.interceptors.request.use(
    async (config) => {
        // First check for ID token (preferred for backend compatibility)
        const idToken = sessionStorage.getItem("idToken");
        if (idToken) {
            console.log("API: Using ID token for backend auth");
            config.headers.Authorization = `Bearer ${idToken}`;
            return config;
        }
        
        // Fallback to access token if ID token not available
        const manualAccessToken = sessionStorage.getItem("accessToken");
        const tokenExpiresAt = sessionStorage.getItem("tokenExpiresAt");
        
        // If we have a valid manual token, use it
        if (manualAccessToken && tokenExpiresAt) {
            const now = new Date().getTime();
            const expiresAt = parseInt(tokenExpiresAt, 10);
            
            if (now < expiresAt) {
                console.log("API: Using manually stored access token");
                config.headers.Authorization = `Bearer ${manualAccessToken}`;
                return config;
            } else {
                console.log("API: Manual token expired, removing");
                // Token expired, handle login redirect
                await redirectToLogin();
                return config;
            }
        }
        
        // If no manual token, try MSAL approach as fallback
        console.log("API: No manual token, trying MSAL");
        
        // Get active account and check if token exists
        const msalInstance = getMsalInstance();
        if (!msalInstance) {
            console.warn("API: MSAL instance not available! Redirecting to login...");
            await redirectToLogin();
            return config;
        }
        const account = msalInstance.getActiveAccount();
        if (!account) {
            console.warn("API: No active account! Redirecting to login...");
            await redirectToLogin();
            return config;
        }
        
        try {
            // Get token silently through MSAL
            const response = await msalInstance.acquireTokenSilent({
                ...loginRequest,
                account: account
            });
            
            // Set the Authorization header
            console.log("API: Got token from MSAL");
            config.headers.Authorization = `Bearer ${response.accessToken}`;
            return config;
        } catch (error) {
            console.error("API: Error acquiring token:", error);
            
            // Check if the error is due to expired token
            if (error.name === "InteractionRequiredAuthError" || 
                error.message?.includes("expired") || 
                error.message?.includes("AADSTS70016")) {
                
                console.log("API: Token expired or interaction required, logging out");
                await redirectToLogin();
            } else {
                // For other errors, also redirect to login
                console.log("API: Authentication error, redirecting to login");
                await redirectToLogin();
            }
            
            return config;
        }
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add response interceptor to handle 401 Unauthorized errors
authApiClient.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        // Check if error is due to an unauthorized request (status 401)
        if (error.response && error.response.status === 401) {
            console.log("API: Received 401 Unauthorized response, redirecting to login");
            
            // Clear tokens and redirect to login
            await redirectToLogin();
            
            // Create a friendly error message
            const customError = new Error("Your session has expired. Please log in again.");
            customError.originalError = error;
            return Promise.reject(customError);
        }
        
        return Promise.reject(error);
    }
);

export default authApiClient;